import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import api, {
  getAdminStats, getAdminUsers, getPendingTeachers, approveTeacher,
  getAdminAnalytics,
  banUser, deleteUser, getAdminCourses, approveCourse, featureCourse,
  deleteAdminCourse, getAdminTransactions, getCourse,
  getCoupons, createCoupon, updateCoupon, deleteCoupon,
  getAllCampaigns, createCampaign, deleteCampaign, updateCampaign,
  uploadCampaignBannerFile,
  getInquiries, updateInquiryStatus, deleteInquiry,
} from "../services/api";
import ProtectedVideoPlayer from "./ProtectedVideoPlayer";
import toast from "react-hot-toast";
import "./AdminPage.css";

const TABS = [
  { key: "overview",     icon: "🏠", label: "الرئيسية"  },
  { key: "analytics",    icon: "📊", label: "التحليلات"  },
  { key: "teachers",     icon: "👨‍🏫", label: "المدرسون"   },
  { key: "courses",      icon: "📚", label: "الكورسات"    },
  { key: "students",     icon: "👨‍🎓", label: "الطلاب"     },
  { key: "transactions", icon: "💰", label: "المعاملات"   },
  { key: "coupons",      icon: "🎟️", label: "الكوبونات"  },
  { key: "campaigns",    icon: "🔥", label: "الحملات"     },
  { key: "reviews",      icon: "⭐", label: "التقييمات"   },
  { key: "messages",     icon: "📩", label: "الرسائل"    },
];

export default function AdminPage() {
  const [tab, setTab]               = useState("overview");
  const [stats, setStats]           = useState(null);
  const [pending, setPending]       = useState([]);
  const [teachers, setTeachers]     = useState([]);
  const [students, setStudents]     = useState([]);
  const [courses, setCourses]       = useState([]);
  const [transactions, setTx]       = useState([]);
  const [txSearch,      setTxSearch]      = useState("");
  const [messages,      setMessages]      = useState([]);
  const [msgFilter,     setMsgFilter]     = useState("all");
  const [loading, setLoading]       = useState(true);
  const [analytics, setAnalytics]   = useState(null);
  const [courseFilter, setCFilter]  = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [previewCourse, setPreview] = useState(null);
  const [previewLesson, setPreviewLesson] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, pr, tr, cr, tx, st, ar, mr] = await Promise.all([
        getAdminStats(), getPendingTeachers(),
        getAdminUsers({ role: "teacher" }), getAdminCourses(),
        getAdminTransactions(), getAdminUsers({ role: "student" }),
        getAdminAnalytics(),
        getInquiries(),
      ]);
      setStats(sr.data);
      setAnalytics(ar.data);
      setPending(pr.data);
      setTeachers(tr.data.users || []);
      setCourses(cr.data);
      setTx(tx.data);
      setStudents(st.data.users || []);
      setMessages(mr.data);
    } catch { toast.error("خطأ في تحميل البيانات"); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const revenueChartData = stats?.monthlyRevenue
    ? Object.entries(stats.monthlyRevenue).sort()
        .map(([m, v]) => ({ شهر: m, إيراد: Math.round(v) }))
    : [];

  const handleApprove = async (id, approved) => {
    try { await approveTeacher(id, approved); toast.success(approved ? "✅ تم قبول المدرّس" : "❌ تم رفض المدرّس"); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handleBan = async (id, banned, name) => {
    if (!window.confirm(banned ? `حظر ${name}؟` : `رفع الحظر عن ${name}؟`)) return;
    try { await banUser(id, banned); toast.success(banned ? `تم حظر ${name}` : `تم رفع الحظر عن ${name}`); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`حذف المستخدم "${name}" نهائياً؟`)) return;
    try { await deleteUser(id); toast.success("تم حذف المستخدم"); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handleApproveCourse = async (id, approved, title) => {
    try { await approveCourse(id, approved); toast.success(approved ? `✅ تم نشر "${title}"` : `⏸ تم إيقاف "${title}"`); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handleFeature = async (id, featured) => {
    try { await featureCourse(id, featured); toast.success(featured ? "⭐ تم التمييز" : "تم إلغاء التمييز"); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handleDeleteCourse = async (id, title) => {
    if (!window.confirm(`حذف كورس "${title}" نهائياً؟`)) return;
    try { await deleteAdminCourse(id); toast.success("تم حذف الكورس"); refresh(); }
    catch { toast.error("حدث خطأ"); }
  };

  const handlePreview = async (courseId) => {
    try { const res = await getCourse(courseId); setPreview(res.data); setPreviewLesson(0); }
    catch { toast.error("فشل تحميل الكورس"); }
  };

  const filteredCourses = courses.filter(c => {
    if (courseFilter === "pending")   return !c.isPublished;
    if (courseFilter === "published") return c.isPublished;
    if (courseFilter === "featured")  return c.isFeatured;
    return true;
  });

  const filteredTeachers = teachers.filter(t => !userSearch || t.name?.includes(userSearch) || t.email?.includes(userSearch));
  const filteredStudents = students.filter(s => !userSearch || s.name?.includes(userSearch) || s.email?.includes(userSearch));
  const filteredTx       = transactions.filter(t => !txSearch || t.studentName?.includes(txSearch) || t.courseTitle?.includes(txSearch));

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
      <div className="spinner" />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="admin-page"
    >
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-inner">
          <div>
            <h1>🛡️ لوحة تحكم الأدمن</h1>
            <p>إدارة كاملة للمنصة — المستخدمين والكورسات والإيرادات</p>
          </div>
          <div className="admin-header-kpis">
            <div className="kpi-mini"><span>{stats?.platformRevenue?.toFixed(0) || 0}</span><span>ج.م إيرادات</span></div>
            <div className="kpi-mini kpi-warn"><span>{pending.length}</span><span>مدرسون معلقون</span></div>
            <div className="kpi-mini kpi-info"><span>{courses.filter(c => !c.isPublished).length}</span><span>كورسات تنتظر</span></div>
          </div>
        </div>
      </div>

      <div className="admin-body">
        {/* Tabs */}
        <div className="admin-tabs-wrap">
          <div className="admin-tabs">
            {TABS.map(t => (
              <button key={t.key} className={`admin-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
                {t.key === "teachers" && pending.length > 0 && <span className="tab-badge">{pending.length}</span>}
                {t.key === "courses"  && courses.filter(c => !c.isPublished).length > 0 && (
                  <span className="tab-badge">{courses.filter(c => !c.isPublished).length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════ OVERVIEW ═══════ */}
        {tab === "overview" && stats && (
          <div className="tab-content">
            <div className="kpi-grid">
              {[
                { icon: "👥",  label: "إجمالي المستخدمين",      val: stats.totalUsers,            color: "blue"  },
                { icon: "👨‍🎓", label: "الطلاب",                  val: stats.totalStudents,         color: "teal"  },
                { icon: "👨‍🏫", label: "المدرسون النشطون",        val: stats.approvedTeachers,      color: "green" },
                { icon: "⏳",  label: "ينتظرون موافقة",         val: stats.pendingTeachers,       color: "warn", link: () => setTab("teachers") },
                { icon: "📚",  label: "الكورسات المنشورة",       val: stats.publishedCourses,      color: "blue"  },
                { icon: "🕐",  label: "كورسات تنتظر موافقة",    val: stats.pendingCourses,        color: "warn", link: () => { setTab("courses"); setCFilter("pending"); } },
                { icon: "🎓",  label: "إجمالي الاشتراكات",      val: stats.totalEnrollments,      color: "teal"  },
                { icon: "💰",  label: "إيرادات المنصة (ج.م)",   val: Math.round(stats.platformRevenue || 0), color: "green" },
              ].map(k => (
                <div key={k.label} className={`kpi-card kpi-${k.color} ${k.link ? "kpi-clickable" : ""}`}
                  onClick={k.link} style={k.link ? { cursor: "pointer" } : {}}>
                  <div className="kpi-icon">{k.icon}</div>
                  <div className="kpi-val">{k.val}</div>
                  <div className="kpi-label">{k.label}</div>
                </div>
              ))}
            </div>
            {revenueChartData.length > 1 && (
              <div className="card" style={{ padding: 24, marginTop: 20 }}>
                <h3 style={{ fontWeight: 800, marginBottom: 16 }}>إيرادات المنصة (آخر 12 شهر)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={revenueChartData}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#1a9bd5" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#1a9bd5" stopOpacity={0}   />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="شهر" tick={{ fontSize: 11, fontFamily: "Cairo" }} />
                    <YAxis tick={{ fontSize: 11, fontFamily: "Cairo" }} />
                    <Tooltip contentStyle={{ fontFamily: "Cairo", fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="إيراد" stroke="#1a9bd5" fill="url(#revGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* ═══════ ANALYTICS ═══════ */}
        {tab === "analytics" && analytics && (
          <div className="tab-content">
            <div className="grid-2">
              {/* User Distribution */}
              <div className="card" style={{ padding: 20 }}>
                <h3 className="db-section-title">توزيع المستخدمين</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'طلاب', value: analytics.userStats.students },
                        { name: 'مدرسين', value: analytics.userStats.teachers },
                        { name: 'أدمن', value: analytics.userStats.admins },
                      ]}
                      cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" label
                    >
                      <Cell fill="#1a9bd5" />
                      <Cell fill="#0dd4b8" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Subject Popularity */}
              <div className="card" style={{ padding: 20 }}>
                <h3 className="db-section-title">المواد الأكثر اشتراكاً</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.subjectData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1a9bd5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue Line Chart */}
            <div className="card" style={{ padding: 20, marginTop: 20 }}>
              <h3 className="db-section-title">نمو أرباح المنصة</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics.revenueData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1a9bd5" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#1a9bd5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#1a9bd5" fillOpacity={1} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ═══════ TEACHERS ═══════ */}
        {tab === "teachers" && (
          <div className="tab-content">
            {pending.length > 0 && (
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <h3 style={{ fontWeight: 800, marginBottom: 14, fontSize: 15 }}>⏳ المدرسون المنتظرون للموافقة ({pending.length})</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {pending.map(t => (
                    <div key={t.uid} className="pending-card card" style={{ padding: 14 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.email}</div>
                        {t.bio && <div style={{ fontSize: 12, marginTop: 4 }}>{t.bio.slice(0, 100)}</div>}
                        {t.subjects?.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                            {t.subjects.map(s => <span key={s} className="badge badge-blue" style={{ fontSize: 11 }}>{s}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="pending-actions" style={{ display: "flex", gap: 8 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleApprove(t.uid, true)}>✅ قبول</button>
                        <button className="btn btn-outline btn-sm" onClick={() => handleApprove(t.uid, false)}>❌ رفض</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="card">
              <div className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, padding: "0 0 14px", borderBottom: "1px solid var(--border)" }}>
                <h3 style={{ fontWeight: 800, fontSize: 15 }}>كل المدرسين ({teachers.length})</h3>
                <input className="form-input" placeholder="بحث..." value={userSearch}
                  onChange={e => setUserSearch(e.target.value)} style={{ maxWidth: 220, fontSize: 13, padding: "8px 12px" }} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead><tr><th>الاسم</th><th>الإيميل</th><th>المواد</th><th>الحالة</th><th>إجراءات</th></tr></thead>
                  <tbody>
                    {filteredTeachers.map(t => (
                      <tr key={t.uid}>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</td>
                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.email}</td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            {(t.subjects || []).slice(0, 3).map(s => <span key={s} className="badge badge-blue" style={{ fontSize: 10 }}>{s}</span>)}
                          </div>
                        </td>
                        <td>
                          {t.isApproved
                            ? <span className="badge badge-green" style={{ fontSize: 11 }}>✅ نشط</span>
                            : <span className="badge badge-amber" style={{ fontSize: 11 }}>⏳ معلق</span>}
                          {t.isBanned && <span className="badge" style={{ background: "#fee2e2", color: "#dc2626", fontSize: 11, marginRight: 4 }}>محظور</span>}
                        </td>
                        <td>
                          <div className="action-btns">
                            {!t.isApproved && <button className="btn btn-xs btn-primary" onClick={() => handleApprove(t.uid, true)}>قبول</button>}
                            <button className={`btn btn-xs ${t.isBanned ? "btn-outline" : "btn-danger"}`} onClick={() => handleBan(t.uid, !t.isBanned, t.name)}>
                              {t.isBanned ? "رفع الحظر" : "حظر"}
                            </button>
                            <button className="btn btn-xs btn-ghost" onClick={() => handleDeleteUser(t.uid, t.name)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ COURSES ═══════ */}
        {tab === "courses" && (
          <div className="tab-content">
            <div className="course-filters-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { key: "all",       label: `الكل (${courses.length})` },
                { key: "pending",   label: `⏳ تنتظر (${courses.filter(c => !c.isPublished).length})` },
                { key: "published", label: `✅ منشورة (${courses.filter(c => c.isPublished).length})` },
                { key: "featured",  label: `⭐ مميزة (${courses.filter(c => c.isFeatured).length})` },
              ].map(f => (
                <button key={f.key} className={`filter-pill ${courseFilter === f.key ? "active" : ""}`}
                  onClick={() => setCFilter(f.key)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid var(--border)", background: courseFilter === f.key ? "var(--blue)" : "#fff", color: courseFilter === f.key ? "#fff" : "var(--text-muted)", fontFamily: "inherit", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="card">
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead><tr><th>الكورس</th><th>المادة</th><th>المدرّس</th><th>السعر</th><th>طلاب</th><th>الحالة</th><th>إجراءات</th></tr></thead>
                  <tbody>
                    {filteredCourses.map(c => (
                      <tr key={c.id}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {c.thumbnail && <img src={c.thumbnail} alt="" className="course-thumb-mini" />}
                            <span className="course-title-cell" style={{ fontSize: 13, fontWeight: 600 }}>{c.title}</span>
                          </div>
                        </td>
                        <td><span className="badge badge-blue" style={{ fontSize: 11 }}>{c.subject}</span></td>
                        <td style={{ fontSize: 12 }}>{c.teacherName}</td>
                        <td style={{ fontWeight: 700, color: "var(--blue)", fontSize: 13 }}>
                          {c.price === 0 ? <span style={{ color: "var(--success)" }}>مجاني</span> : `${c.price} ج.م`}
                        </td>
                        <td style={{ fontSize: 13 }}>{c.enrolledCount || 0}</td>
                        <td>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {c.isPublished ? <span className="badge badge-green" style={{ fontSize: 10 }}>✅ منشور</span>
                              : c.pendingReview ? <span className="badge badge-amber" style={{ fontSize: 10 }}>⏳ مراجعة</span>
                              : <span className="badge badge-amber" style={{ fontSize: 10 }}>📝 مسودة</span>}
                            {c.isFeatured && <span className="badge badge-blue" style={{ fontSize: 10 }}>⭐ مميز</span>}
                          </div>
                        </td>
                        <td>
                          <div className="action-btns">
                            <button className="btn btn-xs btn-outline" onClick={() => handlePreview(c.id)}>معاينة</button>
                            <button className={`btn btn-xs ${c.isPublished ? "btn-outline" : "btn-primary"}`}
                              onClick={() => handleApproveCourse(c.id, !c.isPublished, c.title)}>
                              {c.isPublished ? "إيقاف" : "نشر"}
                            </button>
                            <button className="btn btn-xs btn-ghost" onClick={() => handleFeature(c.id, !c.isFeatured)}>
                              {c.isFeatured ? "إلغاء تمييز" : "تمييز ⭐"}
                            </button>
                            <button className="btn btn-xs btn-ghost" onClick={() => handleDeleteCourse(c.id, c.title)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ STUDENTS ═══════ */}
        {tab === "students" && (
          <div className="tab-content">
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
                <h3 style={{ fontWeight: 800, fontSize: 15 }}>الطلاب ({students.length})</h3>
                <input className="form-input" placeholder="بحث..." value={userSearch}
                  onChange={e => setUserSearch(e.target.value)} style={{ maxWidth: 220, fontSize: 13, padding: "8px 12px" }} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead><tr><th>الاسم</th><th>الإيميل</th><th>تاريخ التسجيل</th><th>الحالة</th><th>إجراءات</th></tr></thead>
                  <tbody>
                    {filteredStudents.map(s => (
                      <tr key={s.uid}>
                        <td style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</td>
                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.email}</td>
                        <td style={{ fontSize: 12 }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString("ar-EG") : "—"}</td>
                        <td>{s.isBanned ? <span className="badge" style={{ background: "#fee2e2", color: "#dc2626", fontSize: 11 }}>محظور</span>
                          : <span className="badge badge-green" style={{ fontSize: 11 }}>نشط</span>}</td>
                        <td>
                          <div className="action-btns">
                            <button className={`btn btn-xs ${s.isBanned ? "btn-outline" : "btn-danger"}`}
                              onClick={() => handleBan(s.uid, !s.isBanned, s.name)}>
                              {s.isBanned ? "رفع الحظر" : "حظر"}
                            </button>
                            <button className="btn btn-xs btn-ghost" onClick={() => handleDeleteUser(s.uid, s.name)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ TRANSACTIONS ═══════ */}
        {tab === "transactions" && (
          <div className="tab-content">
            <div className="tx-summary">
              {[
                { icon: "💳", val: transactions.length,                      lbl: "إجمالي المعاملات" },
                { icon: "💰", val: Math.round(stats?.totalRevenue || 0),     lbl: "إجمالي الإيرادات (ج.م)" },
                { icon: "🏦", val: Math.round(stats?.platformRevenue || 0),  lbl: "حصة المنصة (ج.م)",    cls: "tx-green" },
                { icon: "👨‍🏫",val: Math.round(stats?.teacherPayouts || 0),   lbl: "مدفوعات المدرسين (ج.م)", cls: "tx-blue" },
              ].map(k => (
                <div key={k.lbl} className={`tx-sum-card ${k.cls || ""}`}>
                  <div className="tx-sum-icon">{k.icon}</div>
                  <div className="tx-sum-val">{k.val}</div>
                  <div className="tx-sum-lbl">{k.lbl}</div>
                </div>
              ))}
            </div>
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
                <h3 style={{ fontWeight: 800, fontSize: 15 }}>كل المعاملات ({transactions.length})</h3>
                <input className="form-input" placeholder="بحث باسم الطالب أو الكورس..." value={txSearch}
                  onChange={e => setTxSearch(e.target.value)} style={{ maxWidth: 280, fontSize: 13, padding: "8px 12px" }} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead><tr><th>التاريخ</th><th>الطالب</th><th>الكورس</th><th>المبلغ</th><th>حصة المنصة</th><th>إجراءات</th></tr></thead>
                  <tbody>
                    {filteredTx.slice(0, 100).map((t, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 11 }}>{new Date(t.createdAt).toLocaleDateString("ar-EG")}</td>
                        <td style={{ fontSize: 12 }}>{t.studentName || t.studentId?.slice(0,8)}</td>
                        <td style={{ fontSize: 12 }}>{t.courseTitle || 'كورس'}</td>
                        <td style={{ fontWeight: 700 }}>{t.amount} ج.م</td>
                        <td style={{ color: "var(--success)", fontWeight: 700 }}>{Math.round(t.platformFee)} ج.م</td>
                        <td style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "monospace" }}>{t.paymobTransactionId?.slice(-8) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTx.length === 0 && <div className="db-empty"><div>💰</div><h3>لا توجد معاملات تطابق البحث</h3></div>}
              </div>
            </div>
          </div>
        )}

        {/* ═══════ COUPONS ═══════ */}
        {tab === "coupons" && <CouponsTab />}

        {/* ═══════ CAMPAIGNS ═══════ */}
        {tab === "campaigns" && <CampaignsTab />}

        {/* ═══════ REVIEWS ═══════ */}
        {tab === "reviews" && <ReviewsTab />}

        {/* ═══════ MESSAGES ═══════ */}
        {tab === "messages" && (
          <div className="tab-content">
            <div className="course-filters-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { key: "all",     label: `الكل (${messages.length})` },
                { key: "new",     label: `📩 جديدة (${messages.filter(m => m.status === "new").length})` },
                { key: "read",    label: `📖 مقروءة (${messages.filter(m => m.status === "read").length})` },
                { key: "replied", label: `✅ تم الرد (${messages.filter(m => m.status === "replied").length})` },
              ].map(f => (
                <button key={f.key} className={`filter-pill ${msgFilter === f.key ? "active" : ""}`}
                  onClick={() => setMsgFilter(f.key)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid var(--border)", background: msgFilter === f.key ? "var(--blue)" : "#fff", color: msgFilter === f.key ? "#fff" : "var(--text-muted)", fontFamily: "inherit", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="card">
              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12 }}>
                {messages.filter(m => msgFilter === "all" || m.status === msgFilter).map(m => (
                  <div key={m.id} className="message-card" style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 12, background: m.status === "new" ? "var(--blue-light)" : "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <div>
                        <strong style={{ fontSize: 14 }}>{m.name}</strong>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 8px" }}>·</span>
                        <a href={`mailto:${m.email}`} style={{ fontSize: 12, color: "var(--blue)" }}>{m.email}</a>
                        {m.phone && <span style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 8px" }}>· {m.phone}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{new Date(m.createdAt).toLocaleString("ar-EG")}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>الموضوع: {m.subject}</div>
                    <p style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 12, whiteSpace: "pre-wrap" }}>{m.message}</p>
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        value={m.status}
                        onChange={async (e) => {
                          try { await updateInquiryStatus(m.id, e.target.value); toast.success("تم تحديث الحالة"); refresh(); }
                          catch { toast.error("حدث خطأ"); }
                        }}
                        style={{ padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid var(--border)" }}
                      >
                        <option value="new">جديدة</option>
                        <option value="read">مقروءة</option>
                        <option value="replied">تم الرد</option>
                      </select>
                      <button className="btn btn-xs btn-ghost" onClick={async () => {
                        if (!window.confirm("حذف هذه الرسالة؟")) return;
                        try { await deleteInquiry(m.id); toast.success("تم الحذف"); refresh(); }
                        catch { toast.error("حدث خطأ"); }
                      }}>🗑 حذف</button>
                    </div>
                  </div>
                ))}
                {messages.filter(m => msgFilter === "all" || m.status === msgFilter).length === 0 && (
                  <div className="db-empty"><div>📩</div><h3>لا توجد رسائل</h3></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════ COURSE PREVIEW MODAL ═══════ */}
      {previewCourse && (
        <div className="preview-overlay" onClick={() => setPreview(null)}>
          <div className="preview-modal" onClick={e => e.stopPropagation()}>
            <div className="preview-header">
              <div>
                <h2>{previewCourse.title}</h2>
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <span className="badge badge-blue">{previewCourse.subject}</span>
                  <span className="badge badge-blue">👤 {previewCourse.teacherName}</span>
                  <span className="badge badge-blue">💰 {previewCourse.price === 0 ? "مجاني" : `${previewCourse.price} ج.م`}</span>
                  <span className="badge badge-blue">📖 {previewCourse.lessons?.length || 0} درس</span>
                  {previewCourse.isPublished
                    ? <span className="badge badge-green">✅ منشور</span>
                    : <span className="badge badge-amber">⏳ معلق</span>}
                </div>
              </div>
              <button className="preview-close" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="preview-body">
              {previewCourse.description && <div className="preview-desc">{previewCourse.description}</div>}
              {previewCourse.lessons?.length > 0 ? (
                <div className="preview-lessons-grid">
                  <div className="preview-lesson-list">
                    {previewCourse.lessons.map((lesson, i) => (
                      <button key={i} className={`preview-lesson-btn ${previewLesson === i ? "active" : ""}`}
                        onClick={() => setPreviewLesson(i)}>
                        <span className="preview-lesson-num">{i + 1}</span>
                        <span className="preview-lesson-title">{lesson.title}</span>
                        {lesson.cloudinaryPublicId ? <span className="badge badge-blue" style={{ fontSize: 10 }}>🔒 Cloudinary</span>
                          : lesson.directVideoUrl ? <span className="badge badge-green" style={{ fontSize: 10 }}>🔗 رابط</span>
                          : <span className="badge badge-amber" style={{ fontSize: 10 }}>بدون فيديو</span>}
                        {lesson.duration && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>⏱ {lesson.duration}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="preview-player">
                    {previewCourse.lessons[previewLesson]?.cloudinaryPublicId || previewCourse.lessons[previewLesson]?.directVideoUrl ? (
                      <ProtectedVideoPlayer
                        publicId={previewCourse.lessons[previewLesson]?.cloudinaryPublicId || undefined}
                        directUrl={previewCourse.lessons[previewLesson]?.directVideoUrl || undefined}
                        title={previewCourse.lessons[previewLesson]?.title}
                      />
                    ) : (
                      <div className="preview-no-video"><span>🎬</span><p>لا يوجد فيديو لهذا الدرس</p></div>
                    )}
                    <div className="preview-lesson-info">
                      <strong>{previewCourse.lessons[previewLesson]?.title}</strong>
                      {previewCourse.lessons[previewLesson]?.description && (
                        <p>{previewCourse.lessons[previewLesson].description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="db-empty"><div>📖</div><h3>لا توجد دروس بعد</h3></div>
              )}
              <div className="preview-actions">
                {!previewCourse.isPublished ? (
                  <button className="btn btn-primary" onClick={() => { handleApproveCourse(previewCourse.id, true, previewCourse.title); setPreview(null); }}>
                    ✅ الموافقة ونشر الكورس
                  </button>
                ) : (
                  <button className="btn btn-outline" onClick={() => { handleApproveCourse(previewCourse.id, false, previewCourse.title); setPreview(null); }}>
                    ⏸ إيقاف الكورس
                  </button>
                )}
                <button className="btn btn-ghost" onClick={() => setPreview(null)}>إغلاق</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════════
   COUPONS TAB
══════════════════════════════════════════════════════════ */
function CouponsTab() {
  const [coupons,  setCoupons]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: '', discountType: 'percentage', discountValue: '',
    expirationDate: '', usageLimit: '100', description: '',
  });

  const load = () => {
    setLoading(true);
    getCoupons().then(r => setCoupons(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discountValue || !form.expirationDate)
      return toast.error('الكود، قيمة الخصم، وتاريخ الانتهاء مطلوبة');
    setCreating(true);
    try {
      await createCoupon({
        code:           form.code.toUpperCase().trim(),
        discountType:   form.discountType,
        discountValue:  parseFloat(form.discountValue),
        expirationDate: form.expirationDate,
        usageLimit:     parseInt(form.usageLimit) || 100,
        description:    form.description,
      });
      toast.success(`✅ تم إنشاء الكوبون ${form.code.toUpperCase()}`);
      setForm({ code: '', discountType: 'percentage', discountValue: '', expirationDate: '', usageLimit: '100', description: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setCreating(false);
  };

  const handleToggle = async (id, active) => {
    try { await updateCoupon(id, { active: !active }); toast.success(!active ? 'تم التفعيل' : 'تم الإيقاف'); load(); }
    catch { toast.error('حدث خطأ'); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`حذف الكوبون "${code}"؟`)) return;
    try { await deleteCoupon(id); toast.success('تم الحذف'); load(); }
    catch { toast.error('حدث خطأ'); }
  };

  return (
    <div className="tab-content">
      {/* Create form */}
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontWeight: 800, marginBottom: 18, fontSize: 16 }}>إنشاء كوبون جديد 🎟️</h3>
        <form onSubmit={handleCreate}>
          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label className="form-label">كود الخصم *</label>
              <input className="form-input" placeholder="RAMADAN30" value={form.code}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                style={{ textTransform: 'uppercase', letterSpacing: 1 }} required />
            </div>
            <div className="form-group">
              <label className="form-label">نوع الخصم</label>
              <select className="form-input" value={form.discountType}
                onChange={e => setForm(p => ({ ...p, discountType: e.target.value }))}>
                <option value="percentage">نسبة مئوية (%)</option>
                <option value="fixed">مبلغ ثابت (ج.م)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">قيمة الخصم *</label>
              <input type="number" className="form-input" min={1}
                max={form.discountType === 'percentage' ? 100 : undefined}
                placeholder={form.discountType === 'percentage' ? '30' : '50'}
                value={form.discountValue}
                onChange={e => setForm(p => ({ ...p, discountValue: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">الحد الأقصى للاستخدام</label>
              <input type="number" className="form-input" min={1} value={form.usageLimit}
                onChange={e => setForm(p => ({ ...p, usageLimit: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">تاريخ الانتهاء *</label>
              <input type="date" className="form-input"
                min={new Date().toISOString().split('T')[0]}
                value={form.expirationDate}
                onChange={e => setForm(p => ({ ...p, expirationDate: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">وصف داخلي</label>
              <input className="form-input" placeholder="ملاحظة للأدمن"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: 'inline-block' }} /> جارٍ الإنشاء...</> : '+ إنشاء الكوبون'}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="card" style={{ overflowX: 'auto' }}>
        <h3 style={{ fontWeight: 800, padding: '0 0 14px', borderBottom: '1px solid var(--border)', marginBottom: 14, fontSize: 15 }}>
          الكوبونات ({coupons.length})
        </h3>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : coupons.length === 0 ? (
          <div className="db-empty"><div>🎟️</div><h3>لا توجد كوبونات بعد</h3></div>
        ) : (
          <table className="admin-table">
            <thead><tr><th>الكود</th><th>الخصم</th><th>الاستخدام</th><th>الانتهاء</th><th>الحالة</th><th>إجراءات</th></tr></thead>
            <tbody>
              {coupons.map(c => {
                const expired = c.expirationDate && new Date(c.expirationDate) < new Date();
                const active  = c.active && !expired;
                return (
                  <tr key={c.id}>
                    <td>
                      <code style={{ background: '#f0f9ff', padding: '2px 8px', borderRadius: 5, fontWeight: 700, letterSpacing: 1 }}>{c.code}</code>
                      {c.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.description}</div>}
                    </td>
                    <td><strong>{c.discountType === 'percentage' ? `${c.discountValue}%` : `${c.discountValue} ج.م`}</strong></td>
                    <td style={{ fontSize: 13 }}>{c.usedCount} / {c.usageLimit}</td>
                    <td style={{ fontSize: 12 }}>
                      {c.expirationDate ? new Date(c.expirationDate).toLocaleDateString('ar-EG') : '—'}
                      {expired && <div style={{ color: 'var(--danger)', fontSize: 11 }}>منتهي الصلاحية</div>}
                    </td>
                    <td>
                      <span style={{ background: active ? '#dcfce7' : '#fee2e2', color: active ? '#15803d' : '#dc2626', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                        {active ? '✓ نشط' : expired ? 'منتهي' : 'متوقف'}
                      </span>
                    </td>
                    <td>
                      <div className="action-btns">
                        {!expired && (
                          <button className="btn btn-xs btn-outline" onClick={() => handleToggle(c.id, c.active)}>
                            {c.active ? 'إيقاف' : 'تفعيل'}
                          </button>
                        )}
                        <button className="btn btn-xs btn-ghost" onClick={() => handleDelete(c.id, c.code)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CAMPAIGNS TAB
══════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════
   REVIEWS TAB
══════════════════════════════════════════════════════════ */
function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/reviews');
      setReviews(res.data || []);
    } catch { toast.error("فشل تحميل التقييمات"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, approved) => {
    try {
      await api.put(`/admin/reviews/${id}/approve`, { approved });
      toast.success(approved ? "✅ تم قبول التقييم" : "⏸ تم إيقاف التقييم");
      load();
    } catch { toast.error("حدث خطأ"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("حذف هذا التقييم نهائياً؟")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("تم الحذف");
      load();
    } catch { toast.error("حدث خطأ"); }
  };

  const filtered = reviews.filter(r => {
    if (filter === 'pending') return !r.isApproved;
    if (filter === 'approved') return r.isApproved;
    return true;
  });

  return (
    <div className="tab-content">
      <div className="course-filters-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "all",      label: `الكل (${reviews.length})` },
          { key: "pending",  label: `⏳ ينتظر (${reviews.filter(r => !r.isApproved).length})` },
          { key: "approved", label: `✅ مقبول (${reviews.filter(r => r.isApproved).length})` },
        ].map(f => (
          <button key={f.key} className={`filter-pill ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid var(--border)", background: filter === f.key ? "var(--blue)" : "#fff", color: filter === f.key ? "#fff" : "var(--text-muted)", fontFamily: "inherit", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="db-empty"><div>⭐</div><h3>لا توجد تقييمات</h3></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12 }}>
            {filtered.map(r => (
              <div key={r.id} className="review-admin-card" style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 12, background: r.isApproved ? "#fff" : "#fffbeb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="avatar-mini" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                      {r.userAvatar ? <img src={r.userAvatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} /> : (r.userName?.[0] || '؟')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{r.userName}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.userRole === 'teacher' ? 'مدرّس' : 'طالب'}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: "#f59e0b", fontSize: 16 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{new Date(r.createdAt).toLocaleDateString("ar-EG")}</div>
                  </div>
                </div>
                <p style={{ fontSize: 13, fontStyle: 'italic', margin: '10px 0' }}>"{r.comment}"</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                  <span className={`badge ${r.isApproved ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: 10 }}>
                    {r.isApproved ? '✅ مقبول ومعروض' : '⏳ ينتظر الموافقة'}
                  </span>
                  <div className="action-btns">
                    <button className={`btn btn-xs ${r.isApproved ? "btn-outline" : "btn-primary"}`} onClick={() => handleApprove(r.id, !r.isApproved)}>
                      {r.isApproved ? "إيقاف العرض" : "موافقة ونشر ✅"}
                    </button>
                    <button className="btn btn-xs btn-ghost" onClick={() => handleDelete(r.id)}>🗑 حذف</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [creating,  setCreating]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', startDate: '', endDate: '',
    discountType: 'percentage', discountValue: '',
    bannerImage: '',
  });
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerInputRef = useRef();

  const load = () => {
    setLoading(true);
    getAllCampaigns().then(r => setCampaigns(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append('banner', file);
      const { data } = await uploadCampaignBannerFile(fd);
      setForm(p => ({ ...p, bannerImage: data.url }));
      toast.success('✅ تم رفع الصورة');
    } catch { toast.error('فشل رفع الصورة'); }
    setBannerUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.startDate || !form.endDate || !form.discountValue)
      return toast.error('العنوان، التواريخ، وقيمة الخصم مطلوبة');
    setCreating(true);
    try {
      if (editingId) {
        await updateCampaign(editingId, {
          title:         form.title,
          description:   form.description,
          startDate:     form.startDate,
          endDate:       form.endDate,
          discountType:  form.discountType,
          discountValue: parseFloat(form.discountValue),
          bannerImage:   form.bannerImage,
        });
        toast.success(`✅ تم تحديث الحملة "${form.title}"`);
      } else {
        await createCampaign({
          title:         form.title,
          description:   form.description,
          startDate:     form.startDate,
          endDate:       form.endDate,
          discountType:  form.discountType,
          discountValue: parseFloat(form.discountValue),
          bannerImage:   form.bannerImage,
        });
        toast.success(`✅ تم إنشاء الحملة "${form.title}"`);
      }
      setForm({ title: '', description: '', startDate: '', endDate: '', discountType: 'percentage', discountValue: '', bannerImage: '' });
      setEditingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setCreating(false);
  };

  const handleEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title:         c.title,
      description:   c.description || '',
      startDate:     c.startDate ? new Date(c.startDate).toISOString().split('T')[0] : '',
      endDate:       c.endDate ? new Date(c.endDate).toISOString().split('T')[0] : '',
      discountType:  c.discountType || 'percentage',
      discountValue: c.discountValue || '',
      bannerImage:   c.bannerImage || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`حذف الحملة "${title}"؟`)) return;
    try { await deleteCampaign(id); toast.success('تم الحذف'); load(); }
    catch { toast.error('حدث خطأ'); }
  };

  return (
    <div className="tab-content">
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontWeight: 800, marginBottom: 18, fontSize: 16 }}>
          {editingId ? 'تعديل الحملة ✏️' : 'إنشاء حملة خصم 🔥'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">صورة الحملة (اختياري)</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {form.bannerImage && <img src={form.bannerImage} alt="" style={{ width: 120, height: 60, objectFit: 'cover', borderRadius: 8 }} />}
              <button type="button" className="btn btn-outline btn-sm" onClick={() => bannerInputRef.current?.click()} disabled={bannerUploading}>
                {bannerUploading ? '⏳ جارٍ الرفع...' : '📷 رفع صورة'}
              </button>
              <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && handleBannerUpload(e.target.files[0])} />
            </div>
          </div>
          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label className="form-label">عنوان الحملة *</label>
              <input className="form-input" placeholder="خصومات رمضان" value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">وصف الحملة</label>
              <input className="form-input" placeholder="وصف مختصر" value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">نوع الخصم</label>
              <select className="form-input" value={form.discountType}
                onChange={e => setForm(p => ({ ...p, discountType: e.target.value }))}>
                <option value="percentage">نسبة مئوية (%)</option>
                <option value="fixed">مبلغ ثابت (ج.م)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">قيمة الخصم *</label>
              <input type="number" className="form-input" min={1} placeholder="40"
                value={form.discountValue}
                onChange={e => setForm(p => ({ ...p, discountValue: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">تاريخ البدء *</label>
              <input type="date" className="form-input" value={form.startDate}
                onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">تاريخ الانتهاء *</label>
              <input type="date" className="form-input" value={form.endDate}
                onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: 'inline-block' }} /> جارٍ الحفظ...</>
                : editingId ? 'حفظ التعديلات' : '+ إنشاء الحملة'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-outline" onClick={() => {
                setEditingId(null);
                setForm({ title: '', description: '', startDate: '', endDate: '', discountType: 'percentage', discountValue: '', bannerImage: '' });
              }}>إلغاء</button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h3 style={{ fontWeight: 800, padding: '0 0 14px', borderBottom: '1px solid var(--border)', marginBottom: 14, fontSize: 15 }}>
          الحملات ({campaigns.length})
        </h3>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : campaigns.length === 0 ? (
          <div className="db-empty"><div>🔥</div><h3>لا توجد حملات بعد</h3></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {campaigns.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 12, background: c.isActive ? '#f0fdf4' : '#fff' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {c.discountType === 'percentage' ? `${c.discountValue}%` : `${c.discountValue} ج.م`} خصم
                    · من {new Date(c.startDate).toLocaleDateString('ar-EG')} إلى {new Date(c.endDate).toLocaleDateString('ar-EG')}
                  </div>
                  {c.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{c.description}</div>}
                </div>
                {c.bannerImage && <img src={c.bannerImage} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />}
                <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, flexShrink: 0,
                  background: c.isActive ? '#dcfce7' : c.isExpired ? '#fee2e2' : '#fef3c7',
                  color:      c.isActive ? '#15803d'  : c.isExpired ? '#dc2626'  : '#b45309' }}>
                  {c.isActive ? '🟢 نشطة' : c.isExpired ? '⛔ منتهية' : '🕐 قادمة'}
                </span>
                <div className="action-btns">
                  <button className="btn btn-xs btn-outline" onClick={() => handleEdit(c)}>تعديل</button>
                  <button className="btn btn-xs btn-ghost" onClick={() => handleDelete(c.id, c.title)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
